import { getLs } from "./localStorage"
import { getCookie } from "./klaviyo"
import { getAdInfo } from "./adTracking"

export const paramIsValid = (id: string, expiry: string): boolean => {
  return !!id && !!expiry && new Date() < new Date(expiry)
}

/**
 * Get UTM parameters, automatically selecting the newest source
 * (compares localStorage vs adTracking cookie)
 *
 * @returns Object with UTM parameters or null if none exist
 */
export const getUtmParameters = (): {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_id?: string
} | null => {
  const utm = getLs("utm")
  const adInfo = getAdInfo()

  const utmExpiry = (utm as any)?.expiry
  const adFirstTouch = adInfo.firstTouch

  // Determine which UTM source is newer
  let useAdTrackingUtm = false
  if (adFirstTouch && utmExpiry) {
    // Both exist - compare timestamps
    useAdTrackingUtm = new Date(adFirstTouch) > new Date(utmExpiry)
  } else if (adFirstTouch && !utmExpiry) {
    // Only ad tracking exists
    useAdTrackingUtm = true
  }
  // If only localStorage exists or neither exists, use localStorage (default behavior)

  if (useAdTrackingUtm) {
    // Use UTM data from adTracking (newer)
    const params: any = {}
    if (adInfo.utmSource) params.utm_source = adInfo.utmSource
    if (adInfo.utmMedium) params.utm_medium = adInfo.utmMedium
    if (adInfo.campaign) params.utm_campaign = adInfo.campaign
    if (adInfo.term) params.utm_term = adInfo.term
    if (adInfo.content) params.utm_content = adInfo.content
    if (adInfo.utmId) params.utm_id = adInfo.utmId

    return Object.keys(params).length > 0 ? params : null
  } else if (utm) {
    // Use UTM data from localStorage (newer or only source)
    const params: any = {}
    if ((utm as any).utm_source) params.utm_source = (utm as any).utm_source
    if ((utm as any).utm_medium) params.utm_medium = (utm as any).utm_medium
    if ((utm as any).utm_campaign)
      params.utm_campaign = (utm as any).utm_campaign
    if ((utm as any).utm_term) params.utm_term = (utm as any).utm_term
    if ((utm as any).utm_content) params.utm_content = (utm as any).utm_content
    if ((utm as any).utm_id) params.utm_id = (utm as any).utm_id

    return Object.keys(params).length > 0 ? params : null
  }

  return null
}

export const getParamMetaData = async (
  includeKeys?: string[]
): Promise<
  {
    key: string
    value: String | number
  }[]
> => {
  const metadata: { key: string; value: String | number }[] = []

  const campaign = getLs("campaign") as any
  const campaignId = campaign?.id
  const campaignExpiry = campaign?.expiry
  const campaignSrc = campaign?.source

  if (campaignId && paramIsValid(campaignId, campaignExpiry)) {
    metadata.push({ key: "campaignId", value: campaignId })
    if (campaignSrc) {
      metadata.push({ key: "campaignSrc", value: campaignSrc })
    }
  }

  const personaId = (getLs("persona") as any)?.id
  if (personaId) {
    metadata.push({ key: "personaId", value: personaId })
  }

  let fbp = getCookie("_fbp")
  let fbc = getCookie("_fbc")
  if (!fbc) {
    fbc = getFbc()
  }
  if (fbc) {
    metadata.push({ key: "fbc", value: fbc })
  }
  if (fbp) {
    metadata.push({ key: "fbp", value: fbp })
  }
  const adInfo = getAdInfo()

  if (adInfo.splitTestVariant) {
    metadata.push({ key: "splitTestVariant", value: adInfo.splitTestVariant })
    metadata.push({
      key: "splitTestStatus",
      value: adInfo.splitTestStatus ?? "inactive",
    })
  }
  if (adInfo.landingPage) {
    metadata.push({ key: "adLandingPage", value: adInfo.landingPage })
  }

  // Get UTM parameters using the utility function (automatically uses newest source)
  const utmParams = getUtmParameters()
  if (utmParams) {
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) {
        metadata.push({ key, value })
      }
    })
  }

  const sscid = getLs("sscid")
  const sscidId = (sscid as any)?.id
  const sscidExpiry = (sscid as any)?.expiry
  if (sscidId && paramIsValid("sscid", sscidExpiry)) {
    metadata.push({ key: "sscid", value: sscidId })
  }

  includeKeys = includeKeys || []

  includeKeys.forEach(k => {
    const value = (getLs(k) as any)?.value
    if (value !== null && value !== undefined) {
      metadata.push({ key: k, value: value })
    }
  })

  return metadata
}

export const getFbc = () => {
  const fbc = getLs("fbc") as any
  if (fbc && paramIsValid("fbc", fbc.expiry)) {
    return fbc.value
  }
  const addParams = getAdInfo()
  if (addParams.isFromFacebook && addParams.clickId) {
    return addParams.clickId
  }
  return ""
}

export const getFreshdeskQueryParams = () => {
  let params = { nonce: "", state: "" }

  if (window && window.location) {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams
    params.nonce = searchParams.get("nonce") || ""
    params.state = searchParams.get("state") || ""
  }

  return params
}

export const getQueryParam = (key: string) => {
  let param = ""

  if (typeof window !== "undefined" && window.location) {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams
    param = searchParams.get(key) || ""
  }

  return param
}
